import { findAndReplace } from "mdast-util-find-and-replace";
import type { Root } from "mdast";
import { TAG_RE, WIKILINK_RE } from "./notes";

/**
 * Remark plugin: rewrite `[[Note]]` / `[[Note|alias]]` and `#tag` text into link
 * nodes the preview can style and intercept. Operates on mdast text nodes, so
 * code blocks, inline code, and math are left untouched by design.
 *
 * Wikilinks carry a `wiki:` URL and a `wikilink` / `wikilink wikilink-new` class
 * (the latter when the target doesn't resolve to an existing note). Tags carry a
 * `tag:` URL and the `tag-pill` class. URLs are encoded; the preview decodes and
 * routes them — `urlTransform` there must let `wiki:` / `tag:` through.
 */
export const createRemarkWikiLink = (existingNames: Set<string>) => () => (tree: Root) => {
  findAndReplace(
    tree,
    [
      [
        WIKILINK_RE,
        (_match: string, target: string, alias?: string) => {
          const name = target.trim();
          const resolves = existingNames.has(name.toLowerCase());
          return {
            type: "link",
            url: `wiki:${encodeURIComponent(name)}`,
            title: null,
            data: { hProperties: { className: resolves ? "wikilink" : "wikilink wikilink-new" } },
            children: [{ type: "text", value: (alias ?? target).trim() }],
          };
        },
      ],
      [
        TAG_RE,
        (_match: string, tag: string) => ({
          type: "link",
          url: `tag:${encodeURIComponent(tag)}`,
          title: null,
          data: { hProperties: { className: "tag-pill" } },
          children: [{ type: "text", value: `#${tag}` }],
        }),
      ],
    ],
    // mdast-util-find-and-replace already skips code/inlineCode/math; add nothing.
  );
  return tree;
};
